import { Transition as HistoryTransition } from 'history';
import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useForm } from 'react-hook-form';

import toast from 'react-hot-toast';
import {
  CommentDataFragment,
  MeProviderDataFragment,
  PatientDataFragment,
  useAddCommentMutation,
} from '../../../generated/graphql';
import useBlocker from '../../hooks/useBlocker';
import { resizeTextArea } from '../../lib/form';
import RecordVoiceNoteModal from '../Modals/RecordVoiceNoteModal';
import UnsavedChangesModal from '../Modals/UnsavedChangesModal';
import ToastAlert from '../ToastAlert';
import ChatInputRow from './ChatInputRow';
import ConversationContainer from './ConversationContainer';
import { MediaInfo } from './ImageUploadButton';

interface CommentForm {
  comment: string;
}

interface ChatProps {
  patient: PatientDataFragment;
  comments: CommentDataFragment[];
  chatHeader?: string | null;
  programFollowUpId?: string;
  onCommentAdded?: (commentData?: CommentDataFragment) => void;
  onCommentDeleted?: (commentId?: string) => void;
  getNextChunkOfComments?: () => Promise<void>;
  areCommentsLoading?: boolean;
  className?: string;
  providerUser?: MeProviderDataFragment | null;
}

const Chat: FC<ChatProps> = ({
  patient,
  comments,
  chatHeader = 'Conversation',
  programFollowUpId,
  onCommentAdded,
  onCommentDeleted,
  getNextChunkOfComments,
  areCommentsLoading,
  className,
  providerUser,
}) => {
  const [isRecordVoiceNoteModalOpen, setIsRecordVoiceNoteModalOpen] =
    useState(false);

  const commentRef = useRef<HTMLTextAreaElement | null>(null);
  const commentRefRegistered = Boolean(commentRef.current);

  const [attachedImageMedia, setAttachedImageMedia] =
    useState<MediaInfo | null>(null);

  useEffect(() => {
    resizeTextArea(commentRef);
  }, [commentRefRegistered]);

  const [addCommentMutation, { loading: addCommentMutationLoading }] =
    useAddCommentMutation();

  const {
    reset: resetForm,
    watch,
    register,
    setValue,
    handleSubmit,
    formState: { isDirty },
  } = useForm<CommentForm>({
    mode: 'onSubmit',
    defaultValues: {
      comment: '',
    },
  });

  const { ref: commentRegisterRef, ...commentRegister } = register('comment', {
    required: {
      value: true,
      message: 'Please enter a reply',
    },
    onChange: () => resizeTextArea(commentRef),
    onBlur: () => {
      setValue('comment', watchComment.trim());
      resizeTextArea(commentRef);
    },
  });

  const watchComment = watch('comment');

  const hasUnsavedChanges = Boolean(isDirty);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] =
    useState(false);

  const [navTransition, setNavTransition] = useState<HistoryTransition>();

  const shouldTriggerUnsavedChangesModal = Boolean(
    hasUnsavedChanges && !addCommentMutationLoading,
  );

  const blocker = useCallback(
    (navTransition: HistoryTransition) => {
      if (!isUnsavedChangesModalOpen) {
        setIsUnsavedChangesModalOpen(true);
        setNavTransition(navTransition);
      }
    },
    [shouldTriggerUnsavedChangesModal],
  );

  useBlocker(blocker, shouldTriggerUnsavedChangesModal);

  const sendComment = async (
    text: string | undefined,
    voiceNoteId: string | undefined,
    imageMediaId: string | undefined,
  ) => {
    text = text?.trim();

    if (!(text || voiceNoteId)) {
      return;
    }

    try {
      const { data } = await addCommentMutation({
        variables: {
          input: {
            ...(programFollowUpId
              ? { programFollowUpId }
              : { programId: patient?.programInstanceId }),
            ...(voiceNoteId ? { voiceNoteId } : { text }),
            imageMediaId,
          },
        },
      });

      const newComment = data?.addComment;

      if (newComment) {
        onCommentAdded?.(newComment);
      }
      resetForm();

      // Wait for the form to reset
      setTimeout(() => {
        resizeTextArea(commentRef);
        setAttachedImageMedia(null);
      }, 200);

      if (data?.addComment) {
        toast.custom(({ visible }) => (
          <ToastAlert
            isVisible={visible}
            level="success"
            message={`Successfully sent message!`}
          />
        ));
      }
    } catch (err) {
      console.error('Errors submitting:', err);
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Something went wrong - try again later."
          level="warning"
        />
      ));
    }
  };

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSubmit((formData) =>
          sendComment(formData.comment, undefined, attachedImageMedia?.id),
        )();
      }
    },
    [handleSubmit, attachedImageMedia],
  );

  return (
    <>
      <div className="relative flex h-full w-full flex-grow flex-col px-5 py-6">
        {chatHeader && (
          <div className="mb-4 text-category font-medium">{chatHeader}</div>
        )}

        <ConversationContainer
          patientFirstName={patient?.firstName ?? ''}
          comments={comments}
          onCommentDeleted={onCommentDeleted}
          fullSizeEmptyState={!programFollowUpId}
          getNextChunkOfComments={getNextChunkOfComments}
          areCommentsLoading={areCommentsLoading}
          className={className}
        />

        <ChatInputRow
          avatarName={providerUser?.name}
          avatarImageUrl={providerUser?.profileImageMedia?.url}
          placeholder={`Write a message to ${
            patient?.firstName || 'client'
          }...`}
          attachedImageMedia={attachedImageMedia}
          onAttachedImageMediaChange={setAttachedImageMedia}
          commentValue={watchComment}
          commentRegisterRef={(el) => {
            commentRegisterRef(el);
            commentRef.current = el;
          }}
          commentRegisterRest={commentRegister}
          onCommentKeyDown={handleKeyDown}
          onPostClick={handleSubmit((formData) =>
            sendComment(formData.comment, undefined, attachedImageMedia?.id),
          )}
          isPostDisabled={!isDirty || addCommentMutationLoading}
          showPostButton={Boolean(watchComment)}
          onRecordVoiceNoteClick={() => setIsRecordVoiceNoteModalOpen(true)}
          autoFocus
        />
      </div>

      <RecordVoiceNoteModal
        isModalOpen={isRecordVoiceNoteModalOpen}
        setClosed={() => setIsRecordVoiceNoteModalOpen(false)}
        onPost={(voiceNoteId) =>
          sendComment(undefined, voiceNoteId, attachedImageMedia?.id)
        }
      />
      <UnsavedChangesModal
        isModalOpen={isUnsavedChangesModalOpen}
        setClosed={() => setIsUnsavedChangesModalOpen(false)}
        onConfirm={() => {
          navTransition?.retry();
          resetForm();
          setIsUnsavedChangesModalOpen(false);
        }}
      />
    </>
  );
};

export default Chat;
